import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiX,
  FiBookOpen,
  FiVolume2,
  FiBookmark,
} from 'react-icons/fi';
import { clearProgressCache } from './api/progressAPI';
import {
  markArticleAsReadExternal,
  markArticleAsOpenedExternal,
  updateArticleProgressExternal,
  getArticleProgressExternal,
} from './VocabularyMastery';
import {
  API_BASE_URL,
  checkTokenExpiration,
  fetchWordPronunciation,
  getAuthHeaders,
  logout,
  pronounceWord,
} from '../../../utils/utils';
import { getStoredUser } from '../../../utils/authSession';
import {
  AUTHENTICATED_USER_MISMATCH_DETAIL,
  buildAuthenticatedUserMismatchMessage,
  createAuthenticatedUserMismatchError,
  isAuthenticatedUserMismatchError,
} from '../../../utils/authUserMismatch';
import {
  addFlashcard as apiAddFlashcard,
  fetchFlashcards as apiFetchFlashcards,
} from './api/flashcardAPI';
import {
  buildGreArticleAccessBlockedMessage,
  createGreArticleAccessForbiddenError,
  GRE_ARTICLE_ACCESS_FORBIDDEN_DETAIL,
  GRE_ARTICLE_ACCESS_REDIRECT_PATH,
  isGreArticleAccessForbiddenError,
} from './articleAccessGuard';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import {
  getGreArticlePublicRef,
  resolveGreArticleRouteRef,
} from '../../../utils/greArticlePublicCode';
import {
  getGreArticleCacheKey,
  getGreArticleExercisesCacheKey,
} from './articleCacheKeys';
import {
  trackVocabArticleReread,
  trackVocabRevisionCompleted,
  trackVocabRevisionExercisesStarted,
} from './vocabAnalytics';
import GetStartedWith from '../../../components/GetStartedWith';
import '../../Core.css';

/**
 * Check if a word is a variation of a base vocabulary word.
 * Handles common English morphological variations like plurals, past tense, etc.
 * @param {string} word - The word from the article (cleaned, lowercase)
 * @param {string} baseWord - The vocabulary word to check against (lowercase)
 * @returns {boolean} - True if word is likely a variation of baseWord
 */
const isWordVariation = (word, baseWord) => {
  // Exact match
  if (word === baseWord) return true;

  // Common suffixes for English word variations
  const suffixes = [
    's', // plurals: credential -> credentials
    'es', // plurals: box -> boxes
    'ed', // past tense: credential -> credentialed
    'd', // past tense: assume -> assumed
    'ing', // present participle: run -> running
    'ly', // adverb: quick -> quickly
    'er', // comparative: quick -> quicker
    'est', // superlative: quick -> quickest
    'ness', // noun form: dark -> darkness
    'ment', // noun form: achieve -> achievement
    'tion', // noun form: assume -> assumption
    'sion', // noun form: express -> expression
    'ity', // noun form: active -> activity
    'ies', // plural for y-ending: category -> categories
    'ied', // past tense for y-ending: carry -> carried
    'ying', // -ing for y-ending: carry -> carrying (after dropping y)
    'able', // adjective: accept -> acceptable
    'ible', // adjective: express -> expressible
    'ful', // adjective: hope -> hopeful
    'less', // adjective: hope -> hopeless
    'ive', // adjective: express -> expressive
    'ous', // adjective: danger -> dangerous
    'al', // adjective: accident -> accidental
    'en', // verb/adj: dark -> darken, gold -> golden
    'ize', // verb: modern -> modernize
    'ise', // verb (British): modern -> modernise
    'ization', // noun: modernize -> modernization
    'isation', // noun (British): modernise -> modernisation
    'ance', // noun: rely -> reliance, signify -> significance
    'ence', // noun: differ -> difference, exist -> existence
    'ant', // adjective/noun: rely -> reliant, resist -> resistant
    'ent', // adjective/noun: depend -> dependent, differ -> different
    'ure', // noun: expose -> exposure, compose -> composure
    'ory', // adjective: explain -> explanatory
    'ary', // adjective: imagine -> imaginary
    'ical', // adjective: history -> historical
    'ively', // adverb: express -> expressively
    'ively', // adverb: active -> actively
    'ousness', // noun: conscious -> consciousness
    'iveness', // noun: expressive -> expressiveness
  ];

  // Check if word is baseWord + suffix
  for (const suffix of suffixes) {
    // Direct suffix: word = base + suffix (e.g., credentials = credential + s)
    if (word === baseWord + suffix) return true;

    // Handle consonant doubling (e.g., run -> running)
    const lastChar = baseWord.slice(-1);
    if (['b', 'd', 'g', 'l', 'm', 'n', 'p', 'r', 't'].includes(lastChar)) {
      if (word === baseWord + lastChar + suffix) return true;
    }

    // Handle e-dropping (e.g., assume -> assuming, make -> making)
    if (baseWord.endsWith('e')) {
      const stemWithoutE = baseWord.slice(0, -1);
      if (word === stemWithoutE + suffix) return true;
    }

    // Handle y -> i replacement (e.g., carry -> carried, happy -> happiness)
    if (baseWord.endsWith('y')) {
      const stemWithoutY = baseWord.slice(0, -1);
      // y -> i before certain suffixes
      if (['ed', 'es', 'er', 'est', 'ness', 'ly'].includes(suffix)) {
        if (word === stemWithoutY + 'i' + suffix) return true;
      }
      // y -> ie before s (e.g., category -> categories handled by 'ies' suffix)
    }

    // Handle -ic -> -ical (e.g., historic -> historical)
    if (baseWord.endsWith('ic') && suffix === 'al') {
      if (word === baseWord + 'al') return true;
    }
  }

  // Check if removing common suffixes from word gives baseWord
  const suffixesToRemove = [
    's',
    'es',
    'ed',
    'd',
    'ing',
    'ly',
    'er',
    'est',
    'ness',
    'ment',
    'tion',
    'sion',
    'ity',
    'ies',
    'ied',
    'able',
    'ible',
    'ful',
    'less',
    'ive',
    'ous',
    'al',
    'en',
    'ize',
    'ise',
    'ization',
    'isation',
    'ification',
    'ance',
    'ence',
    'ant',
    'ent',
    'ure',
    'ory',
    'ary',
    'ical',
    'ively',
    'ousness',
    'iveness',
    'ivity',
    'osity',
  ];
  for (const suffix of suffixesToRemove) {
    if (word.endsWith(suffix)) {
      const stem = word.slice(0, -suffix.length);
      // Direct match after removing suffix
      if (stem === baseWord) return true;
      // Match with e added back (e.g., assuming -> assum -> assume)
      if (stem + 'e' === baseWord) return true;
      // Match with y added back (e.g., carried -> carri -> carry)
      if (suffix === 'ied' || suffix === 'ies') {
        if (stem + 'y' === baseWord) return true;
      }
      // Match with doubled consonant reduced (e.g., running -> runn -> run)
      if (stem.length >= 2 && stem.slice(-1) === stem.slice(-2, -1)) {
        if (stem.slice(0, -1) === baseWord) return true;
      }
      // Handle -ate -> -ation transformation (e.g., allocate -> allocation)
      if (suffix === 'tion' && stem + 'ate' === baseWord) return true;
      // Handle -te -> -tion transformation (e.g., constitute -> constitution)
      if (suffix === 'tion' && stem + 'te' === baseWord) return true;
      // Handle -e -> -ion transformation (e.g., decide -> decision)
      if (suffix === 'ion' && stem + 'e' === baseWord) return true;

      // Handle -ify -> -ification (e.g., clarify -> clarification)
      if (suffix === 'ification' && stem + 'ify' === baseWord) return true;
      if (suffix === 'ification' && stem + 'y' === baseWord) return true;

      // Handle -ize/-ise -> -ization/-isation (e.g., modernize -> modernization)
      if (suffix === 'ization' && stem + 'ize' === baseWord) return true;
      if (suffix === 'isation' && stem + 'ise' === baseWord) return true;

      // Handle -ance/-ence from verbs (e.g., differ -> difference, rely -> reliance)
      if ((suffix === 'ance' || suffix === 'ence') && stem === baseWord)
        return true;
      if ((suffix === 'ance' || suffix === 'ence') && stem + 'e' === baseWord)
        return true;
      if (suffix === 'ance' && stem + 'y' === baseWord) return true; // rely -> reliance

      // Handle -ant/-ent from verbs (e.g., depend -> dependent, resist -> resistant)
      if ((suffix === 'ant' || suffix === 'ent') && stem === baseWord)
        return true;
      if ((suffix === 'ant' || suffix === 'ent') && stem + 'e' === baseWord)
        return true;

      // Handle -ure from verbs (e.g., expose -> exposure, compose -> composure)
      if (suffix === 'ure' && stem + 'e' === baseWord) return true;

      // Handle -ory/-ary from verbs (e.g., explain -> explanatory)
      if ((suffix === 'ory' || suffix === 'ary') && stem === baseWord)
        return true;
      if ((suffix === 'ory' || suffix === 'ary') && stem + 'e' === baseWord)
        return true;

      // Handle -ive -> -ivity (e.g., productive -> productivity)
      if (suffix === 'ivity' && stem + 'ive' === baseWord) return true;

      // Handle -ous -> -osity (e.g., curious -> curiosity)
      if (suffix === 'osity' && stem + 'ous' === baseWord) return true;

      // Handle -ical from -ic (e.g., history -> historical)
      if (suffix === 'ical' && stem + 'y' === baseWord) return true;
      if (suffix === 'ical' && stem + 'ic' === baseWord) return true;
    }
  }

  // Handle -ation words where base ends in -ate (allocation from allocate)
  if (word.endsWith('ation')) {
    const stem = word.slice(0, -5); // remove 'ation'
    if (stem + 'ate' === baseWord) return true;
  }

  // Handle -ion words where base ends in -e (constitution from constitute)
  if (
    word.endsWith('ion') &&
    !word.endsWith('tion') &&
    !word.endsWith('sion')
  ) {
    const stem = word.slice(0, -3); // remove 'ion'
    if (stem + 'e' === baseWord) return true;
  }

  // Handle -sion irregular transformations
  if (word.endsWith('sion')) {
    const stem = word.slice(0, -4); // remove 'sion'
    // -de -> -sion (e.g., conclude -> conclusion, include -> inclusion)
    if (stem + 'de' === baseWord) return true;
    // -mit -> -mission (e.g., permit -> permission, admit -> admission)
    if (word.endsWith('mission')) {
      const missionStem = word.slice(0, -7); // remove 'mission'
      if (missionStem + 'mit' === baseWord) return true;
    }
    // -ss -> -ssion (e.g., express -> expression, possess -> possession)
    if (stem + 's' === baseWord) return true;
  }

  // Handle -ption transformations
  if (word.endsWith('ption')) {
    const stem = word.slice(0, -5); // remove 'ption'
    // -ume -> -umption (e.g., assume -> assumption, consume -> consumption)
    if (stem + 'ume' === baseWord) return true;
    // -ceive -> -ception (e.g., receive -> reception, perceive -> perception)
    if (word.endsWith('ception')) {
      const ceptionStem = word.slice(0, -7); // remove 'ception'
      if (ceptionStem + 'ceive' === baseWord) return true;
    }
  }

  // Handle -ification from -ify (clarification from clarify)
  if (word.endsWith('ification')) {
    const stem = word.slice(0, -9); // remove 'ification'
    if (stem + 'ify' === baseWord) return true;
    if (stem + 'y' === baseWord) return true;
  }

  // ============================================
  // REVERSE DIRECTION: Check if baseWord is a variation of word
  // (e.g., vocab word is "allocation" but text has "allocate")
  // ============================================

  // Check reverse suffix transformations on baseWord
  const reverseSuffixes = [
    's',
    'es',
    'ed',
    'd',
    'ing',
    'ly',
    'er',
    'est',
    'ness',
    'ment',
    'tion',
    'sion',
    'ity',
    'ies',
    'ied',
    'able',
    'ible',
    'ful',
    'less',
    'ive',
    'ous',
    'al',
    'en',
    'ize',
    'ise',
    'ization',
    'isation',
    'ification',
    'ance',
    'ence',
    'ant',
    'ent',
    'ure',
    'ory',
    'ary',
    'ical',
    'ively',
    'ousness',
    'iveness',
    'ivity',
    'osity',
  ];

  for (const suffix of reverseSuffixes) {
    if (baseWord.endsWith(suffix)) {
      const stem = baseWord.slice(0, -suffix.length);
      // Direct match: management -> manag, word is "manage" won't match
      // But with e added: manag + e = manage ✓
      if (stem === word) return true;
      if (stem + 'e' === word) return true;
      if (stem + 'y' === word) return true; // carry from carried

      // Handle -ation -> -ate (allocation -> allocate)
      if (suffix === 'tion' && stem + 'ate' === word) return true;
      if (suffix === 'tion' && stem + 'te' === word) return true;

      // Handle -ification -> -ify (clarification -> clarify)
      if (suffix === 'ification' && stem + 'ify' === word) return true;

      // Handle -ization -> -ize (modernization -> modernize)
      if (suffix === 'ization' && stem + 'ize' === word) return true;
      if (suffix === 'isation' && stem + 'ise' === word) return true;

      // Handle -sion irregulars
      if (suffix === 'sion') {
        if (stem + 'de' === word) return true; // conclusion -> conclude
        if (stem + 's' === word) return true; // expression -> express
      }

      // Handle -ption irregulars
      if (suffix === 'ption') {
        if (stem + 'ume' === word) return true; // assumption -> assume
      }

      // Handle -ivity -> -ive (productivity -> productive)
      if (suffix === 'ivity' && stem + 'ive' === word) return true;

      // Handle -osity -> -ous (curiosity -> curious)
      if (suffix === 'osity' && stem + 'ous' === word) return true;
    }
  }

  // Handle reverse -ation specifically (allocation -> allocate)
  if (baseWord.endsWith('ation')) {
    const stem = baseWord.slice(0, -5);
    if (stem + 'ate' === word) return true;
  }

  // Handle reverse -mission -> -mit (permission -> permit)
  if (baseWord.endsWith('mission')) {
    const stem = baseWord.slice(0, -7);
    if (stem + 'mit' === word) return true;
  }

  // Handle reverse -ception -> -ceive (reception -> receive)
  if (baseWord.endsWith('ception')) {
    const stem = baseWord.slice(0, -7);
    if (stem + 'ceive' === word) return true;
  }

  return false;
};

function VocabularyDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { articleId } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const isRevisionMode = searchParams.get('source') === 'history';
  const requestedStep = searchParams.get('step');
  const [currentArticle, setCurrentArticle] = useState(null);
  const [exercises, setExercises] = useState([]);
  const [currentStep, setCurrentStep] = useState(
    requestedStep === 'exercise' ? 'exercise' : 'article'
  );
  const [exerciseIndex, setExerciseIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [showFeedback, setShowFeedback] = useState(false);
  const [completedExercises, setCompletedExercises] = useState([]);
  const [showDefinition, setShowDefinition] = useState(null);
  const [selectionPronunciation, setSelectionPronunciation] = useState(null);
  const [wordPhonetics, setWordPhonetics] = useState({});
  const [definitionPlacement, setDefinitionPlacement] = useState('above');
  const [definitionMaxHeight, setDefinitionMaxHeight] = useState(420);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [dragPosition, setDragPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const definitionRef = useRef(null);
  const articleBodyRef = useRef(null);
  const selectionPopupRef = useRef(null);
  const selectionRequestIdRef = useRef(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exerciseStartTime, setExerciseStartTime] = useState(null);
  const [randomizedOptions, setRandomizedOptions] = useState([]);
  const user = getStoredUser();

  // Reading time tracking states
  const [articleStartTime, setArticleStartTime] = useState(null);
  const [totalDefinitionTime, setTotalDefinitionTime] = useState(0);
  const [definitionStartTime, setDefinitionStartTime] = useState(null);
  const [articleWPM, setArticleWPM] = useState(null);

  const userEmail = user?.email || null;

  const feedbackRef = useRef(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: '',
    success: true,
  });
  const [authMismatch, setAuthMismatch] = useState(null);
  const [articleAccessBlocked, setArticleAccessBlocked] = useState(null);
  const [addedToFlashcards, setAddedToFlashcards] = useState([]);

  // Refs to track ongoing API requests
  const ongoingRequests = useRef({});
  const blockedAccessRedirectTimeoutRef = useRef(null);
  const authRedirectStartedRef = useRef(false);

  const RC_TYPE_LABELS = {
    main_idea: 'Main Idea',
    inference: 'Inference',
    vocab_in_context: 'Vocabulary in Context',
    sentence_selection: 'Sentence Selection',
    strengthen_weaken: 'Strengthen / Weaken',
  };

  const normalizeAndCombineExercises = (rawData) => {
    let wordExercises = [];
    let rcExercises = [];

    if (Array.isArray(rawData)) {
      wordExercises = rawData.map((ex) => ({ ...ex, _exerciseType: 'word' }));
    } else if (rawData && typeof rawData === 'object') {
      wordExercises = (rawData.word_exercises || []).map((ex) => ({
        ...ex,
        _exerciseType: 'word',
      }));
      rcExercises = (rawData.rc_exercises || []).map((ex) => {
        const isMultiSelect =
          Array.isArray(ex.correct_answers) && ex.correct_answers.length > 1;
        const correctAnswerTexts = (ex.correct_answers || [])
          .map((i) => (ex.options || [])[i])
          .filter(Boolean);
        return {
          ...ex,
          _exerciseType: 'rc',
          _isMultiSelect: isMultiSelect,
          _correctAnswerTexts: correctAnswerTexts,
          correct_answer:
            !isMultiSelect && correctAnswerTexts.length > 0
              ? correctAnswerTexts[0]
              : undefined,
        };
      });
    }

    const combined = [];
    const maxLen = Math.max(wordExercises.length, rcExercises.length);
    for (let i = 0; i < maxLen; i++) {
      if (i < wordExercises.length) combined.push(wordExercises[i]);
      if (i < rcExercises.length) combined.push(rcExercises[i]);
    }

    for (let i = combined.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [combined[i], combined[j]] = [combined[j], combined[i]];
    }

    return combined;
  };

  const toastdisplay = (message, success, duration) => {
    setToast({ show: true, message, success });
    setTimeout(
      () => setToast({ show: false, message: '', success: true }),
      duration
    );
  };

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (blockedAccessRedirectTimeoutRef.current) {
        window.clearTimeout(blockedAccessRedirectTimeoutRef.current);
      }
    };
  }, []);

  const normalizedEmail = React.useMemo(() => {
    const raw = userEmail || '';
    const n = raw.trim().toLowerCase();
    return (n && n !== 'undefined' && n !== 'null') ? n : null;
  }, [userEmail]);

  // Returns one of:
  //   'redirected' — a session-expired redirect to /login was triggered
  //   'refreshed'  — token was silently refreshed; caller should retry
  //   'unhandled'  — caller should treat the original 401 as a real error
  const handleUnauthorizedArticleResponse = async (response) => {
    if (authRedirectStartedRef.current) {
      return 'redirected';
    }

    const tokenExpired = await checkTokenExpiration(
      response,
      (nextValue) => {
        authRedirectStartedRef.current = nextValue;
        setSessionExpired(nextValue);
      },
      setToast
    );

    if (tokenExpired === 'refreshed') {
      return 'refreshed';
    }

    if (tokenExpired) {
      authRedirectStartedRef.current = true;
      return 'redirected';
    }

    return 'unhandled';
  };

  const addToFlashcards = async (wordData) => {
    if (!normalizedEmail) {
      toastdisplay('Please log in to save flashcards', false, 2000);
      return;
    }
    try {
      const cardData = {
        word: wordData.word,
        definition_en: wordData.definition_en,
        definition_fr: wordData.definition_fr,
        example_sentence_en: wordData.example_sentence_en,
        example_sentence_fr: wordData.example_sentence_fr,
        difficulty_level: wordData.difficulty_level || 'medium',
        addedAt: new Date().toISOString(),
        question: wordData.question || null,
        options: wordData.options?.length ? wordData.options : null,
      };

      const result = await apiAddFlashcard(normalizedEmail, cardData);
      if (result.success === false && result.message === 'Word already in flashcards') {
        toastdisplay('Word already in flashcards!', false, 2000);
        return;
      }
      if (isMountedRef.current) {
        setAddedToFlashcards((prev) => [...prev, wordData.word]);
      }
      toastdisplay('Added to flashcards!', true, 2000);
    } catch (error) {
      console.error('Error adding to flashcards:', error);
      toastdisplay('Failed to add to flashcards', false, 2000);
    }
  };

  useEffect(() => {
    if (!normalizedEmail) return;
    let cancelled = false;
    apiFetchFlashcards(normalizedEmail)
      .then((cards) => {
        if (!cancelled && isMountedRef.current) {
          setAddedToFlashcards(cards.map((card) => card.word));
        }
      })
      .catch((error) => {
        console.error('Error loading flashcards:', error);
      });
    return () => { cancelled = true; };
  }, [normalizedEmail]);

  // API functions
  const getArticleById = async (articleRef, attempt = 0) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gre/article/${articleRef}`,
        {
          method: 'GET',
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          const outcome = await handleUnauthorizedArticleResponse(response);
          if (outcome === 'redirected') {
            return null;
          }
          if (outcome === 'refreshed' && attempt === 0) {
            return getArticleById(articleRef, attempt + 1);
          }
        }

        const errorData = await response.json().catch(() => ({}));
        if (
          response.status === 403 &&
          errorData.detail === GRE_ARTICLE_ACCESS_FORBIDDEN_DETAIL
        ) {
          throw createGreArticleAccessForbiddenError({
            resourceLabel: 'article',
          });
        }
        if (
          response.status === 403 &&
          errorData.detail === AUTHENTICATED_USER_MISMATCH_DETAIL
        ) {
          throw createAuthenticatedUserMismatchError({
            resourceLabel: 'article',
          });
        }

        throw new Error(errorData.detail || 'Failed to fetch article');
      }

      const data = await response.json();
      // Backend may return either the article object directly or wrap it.
      return data?.article ?? data;
    } catch (error) {
      console.error('Error fetching article:', error);
      throw error;
    }
  };

  const getArticleExercises = async (articleRef, attempt = 0) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/gre/exercises/article/${articleRef}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          const outcome = await handleUnauthorizedArticleResponse(response);
          if (outcome === 'redirected') {
            return null;
          }
          if (outcome === 'refreshed' && attempt === 0) {
            return getArticleExercises(articleRef, attempt + 1);
          }
        }

        const errorData = await response.json().catch(() => ({}));
        if (
          response.status === 403 &&
          errorData.detail === GRE_ARTICLE_ACCESS_FORBIDDEN_DETAIL
        ) {
          throw createGreArticleAccessForbiddenError({
            resourceLabel: 'article exercises',
          });
        }
        if (
          response.status === 403 &&
          errorData.detail === AUTHENTICATED_USER_MISMATCH_DETAIL
        ) {
          throw createAuthenticatedUserMismatchError({
            resourceLabel: 'article exercises',
          });
        }

        throw new Error(errorData.detail || 'Failed to fetch article exercises');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching article exercises:', error);
      throw error;
    }
  };

  const recordExerciseAttempt = async (
    exerciseId,
    userAnswer,
    isCorrect,
    timeTaken,
    articleId,
    exerciseType = 'article_word'
  ) => {
    try {
      const params = new URLSearchParams({
        exercise_id: exerciseId,
        exercise_type: exerciseType,
        user_answer: userAnswer,
        is_correct: isCorrect,
        time_taken: timeTaken,
        article_id: articleId,
      });

      // Add WPM if calculated
      if (articleWPM !== null) {
        params.append('reading_wpm', articleWPM);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/gre/exercises/attempt?${params.toString()}`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to record exercise attempt');
      }

      return await response.json();
    } catch (error) {
      console.error('Error recording exercise attempt:', error);
      throw error;
    }
  };

  // Enhanced API functions with duplicate prevention
  const getArticleByIdWithRef = async (articleId) => {
    const requestKey = `article_${articleId}`;
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    const promise = new Promise((resolve, reject) => {
      const articleCacheKey = getGreArticleCacheKey(user, articleId);

      // Try to get from localStorage first
      const cachedData = localStorage.getItem(articleCacheKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          const cachedContent = (parsed?.content ?? '').trim();
          // If cache is missing the full article content, refresh from backend.
          if (cachedContent) {
            return resolve(parsed);
          }
        } catch (e) {
          console.error(
            'Failed to parse cached article, fetching from backend...',
            e
          );
        }
      }

      // If not in cache or parse failed, fetch from backend
      getArticleById(articleId)
        .then((article) => {
          const normalized = article?.article ?? article;
          if (normalized) {
            const canonicalArticleRef =
              getGreArticlePublicRef(normalized) || articleId;
            localStorage.setItem(
              getGreArticleCacheKey(user, canonicalArticleRef),
              JSON.stringify(normalized)
            );
            resolve(normalized);
          } else {
            reject(new Error(`Article with ID ${articleId} not found.`));
          }
        })
        .catch(reject);
    });

    ongoingRequests.current[requestKey] = promise;
    promise.finally(() => {
      delete ongoingRequests.current[requestKey];
    });

    return promise;
  };

  const getArticleExercisesWithRef = async (articleId) => {
    const requestKey = `exercises_${articleId}`;
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    const promise = new Promise((resolve, reject) => {
      const exercisesCacheKey = getGreArticleExercisesCacheKey(user, articleId);
      const cachedData = localStorage.getItem(exercisesCacheKey);
      if (cachedData) {
        try {
          const parsed = JSON.parse(cachedData);
          if (parsed && !Array.isArray(parsed) && parsed.word_exercises) {
            return resolve(parsed);
          }
        } catch (e) {
          console.error(
            'Failed to parse cached exercises, fetching from backend...',
            e
          );
        }
      }

      getArticleExercises(articleId)
        .then((exercises) => {
          const normalized = exercises || {
            word_exercises: [],
            rc_exercises: [],
          };
          const canonicalArticleRef = resolveGreArticleRouteRef(articleId) || articleId;
          localStorage.setItem(
            getGreArticleExercisesCacheKey(user, canonicalArticleRef),
            JSON.stringify(normalized)
          );
          resolve(normalized);
        })
        .catch(reject);
    });

    ongoingRequests.current[requestKey] = promise;
    promise.finally(() => {
      delete ongoingRequests.current[requestKey];
    });

    return promise;
  };

  const getDailyArticlesWithRef = async () => {
    const requestKey = 'daily_articles';

    // If request is already in progress, return the existing promise
    if (ongoingRequests.current[requestKey]) {
      return ongoingRequests.current[requestKey];
    }

    // Create new request promise
    const requestPromise = fetch(`${API_BASE_URL}/api/gre/program-window`, {
      headers: {
        ...getAuthHeaders(),
      },
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error('window');
        }
        return res.json();
      })
      .then((data) => {
        const offset = Number(sessionStorage.getItem('gre_program_offset') || 0);
        const day = (data.days || []).find((item) => item.offset === offset);
        return day?.items || [];
      })
      .catch(() =>
        fetch(`${API_BASE_URL}/api/gre/daily-articles`, {
          headers: {
            ...getAuthHeaders(),
          },
        }).then((res) => res.json())
      );

    // Store the promise in ref
    ongoingRequests.current[requestKey] = requestPromise;

    // Clean up the ref when request completes (success or failure)
    requestPromise.finally(() => {
      ongoingRequests.current[requestKey] = null;
    });

    return requestPromise;
  };

  useEffect(() => {
    const loadArticleData = async () => {
      try {
        setLoading(true);
        setAuthMismatch(null);
        setArticleAccessBlocked(null);

        const params = new URLSearchParams(location.search);
        let articleRef =
          resolveGreArticleRouteRef(articleId) ||
          resolveGreArticleRouteRef(params.get('article')) ||
          resolveGreArticleRouteRef(params.get('article_id'));

        if (!articleRef) {
          const dailyArticles = await getDailyArticlesWithRef();

          if (dailyArticles.length > 0) {
            articleRef = getGreArticlePublicRef(dailyArticles[0]);
          } else {
            throw new Error('No articles available');
          }
        }

        const [article, articleExercisesRaw] = await Promise.all([
          getArticleByIdWithRef(articleRef),
          getArticleExercisesWithRef(articleRef),
        ]);

        if (authRedirectStartedRef.current) {
          return;
        }

        if (!article) {
          throw new Error('Article not found');
        }

        const canonicalArticleRef = getGreArticlePublicRef(article);
        const sourceParam = params.get('source');
        const stepParam = params.get('step');
        if (canonicalArticleRef && params.get('article') !== canonicalArticleRef) {
          const nextParams = new URLSearchParams();
          nextParams.set('article', canonicalArticleRef);
          if (sourceParam) {
            nextParams.set('source', sourceParam);
          }
          if (stepParam) {
            nextParams.set('step', stepParam);
          }
          navigate(
            `/exams/gre/verbal/vocabulary/dashboard/?${nextParams.toString()}`,
            { replace: true }
          );
        }

        const resolvedArticleId = article.id;

        setCurrentArticle(article);
        const combinedExercises =
          normalizeAndCombineExercises(articleExercisesRaw);
        setExercises(combinedExercises);

        if (sourceParam === 'history') {
          trackVocabArticleReread({
            article_public_code: canonicalArticleRef || articleRef,
          });
          if (stepParam === 'exercise') {
            setCurrentStep('exercise');
            trackVocabRevisionExercisesStarted({
              article_public_code: canonicalArticleRef || articleRef,
            });
          }
        }

        markArticleAsOpenedExternal(resolvedArticleId);

        const existingProgress = getArticleProgressExternal(resolvedArticleId);
        const completedExerciseIds = Array.isArray(
          existingProgress.completedExerciseIds
        )
          ? Array.from(new Set(existingProgress.completedExerciseIds.map(String)))
          : [];
        const masteredWordExerciseIds = Array.isArray(
          existingProgress.masteredWordExerciseIds
        )
          ? Array.from(
              new Set(existingProgress.masteredWordExerciseIds.map(String))
            )
          : [];
        updateArticleProgressExternal(resolvedArticleId, {
          totalExercises: combinedExercises.length,
          totalWords: article.words?.length || 0,
          completedExerciseIds,
          masteredWordExerciseIds,
          exercisesCompleted: Math.min(
            completedExerciseIds.length || existingProgress.exercisesCompleted || 0,
            combinedExercises.length
          ),
          wordsMastered: Math.min(
            masteredWordExerciseIds.length || existingProgress.wordsMastered || 0,
            article.words?.length || 0
          ),
        });
      } catch (error) {
        console.error('Error loading article data:', error);
        if (isGreArticleAccessForbiddenError(error)) {
          const blockedMessage = buildGreArticleAccessBlockedMessage({
            resourceLabel: error.resourceLabel || 'article',
          });

          setArticleAccessBlocked({
            message: blockedMessage,
            resourceLabel: error.resourceLabel || 'article',
          });
          setError(blockedMessage);

          if (blockedAccessRedirectTimeoutRef.current) {
            window.clearTimeout(blockedAccessRedirectTimeoutRef.current);
          }

          blockedAccessRedirectTimeoutRef.current = window.setTimeout(() => {
            navigate(GRE_ARTICLE_ACCESS_REDIRECT_PATH, { replace: true });
          }, 1800);
        } else if (isAuthenticatedUserMismatchError(error)) {
          const mismatchState = {
            currentEmail: error.currentEmail || user?.email || null,
            requestedEmail: error.requestedEmail || null,
            resourceLabel: error.resourceLabel || 'article',
          };
          const mismatchMessage =
            buildAuthenticatedUserMismatchMessage(mismatchState);

          setAuthMismatch({
            ...mismatchState,
            message: mismatchMessage,
          });
          setError(mismatchMessage);

          const shouldSwitch = window.confirm(
            `${mismatchMessage}\n\nDo you want to log out and sign in with the correct account to continue?`
          );

          if (shouldSwitch) {
            await logout();
            return;
          }
        } else {
          setError(error.message);
        }
      } finally {
        setLoading(false);
      }
    };

    loadArticleData();
  }, [articleId, location.search, navigate, user?.email]);

  // Initialize article reading time when article is loaded
  useEffect(() => {
    if (currentArticle && currentStep === 'article') {
      setArticleStartTime(Date.now());
      setTotalDefinitionTime(0);
    }
  }, [currentArticle, currentStep]);

  // Calculate WPM function
  const calculateArticleWPM = () => {
    if (!articleStartTime || !currentArticle) return null;

    const currentTime = Date.now();
    const totalReadingTime = currentTime - articleStartTime; // in milliseconds
    const effectiveReadingTime = totalReadingTime - totalDefinitionTime; // exclude definition time

    if (effectiveReadingTime <= 0) return null;

    const safeContent = (currentArticle?.content ?? '').trim();
    const wordCount = safeContent ? safeContent.split(/\s+/).length : 0;
    if (!wordCount) return null;
    const readingTimeMinutes = effectiveReadingTime / (1000 * 60); // convert to minutes
    const wpm = Math.round(wordCount / readingTimeMinutes);

    return wpm > 0 ? wpm : null;
  };

  useEffect(() => {
    if (showFeedback && feedbackRef.current) {
      feedbackRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [showFeedback]);

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Drag functionality
  const handleDragStart = (e) => {
    setIsDragging(true);
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setDragStart({ x: clientX - dragPosition.x, y: clientY - dragPosition.y });
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;
    setDragPosition({
      x: clientX - dragStart.x,
      y: clientY - dragStart.y,
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  // Reset drag position when showing new definition
  useEffect(() => {
    if (showDefinition) {
      setDragPosition({ x: 0, y: 0 });
    }
  }, [showDefinition]);

  // Add global event listeners for drag functionality
  useEffect(() => {
    if (isDragging) {
      const handleMouseMove = (e) => handleDragMove(e);
      const handleMouseUp = () => handleDragEnd();
      const handleTouchMove = (e) => handleDragMove(e);
      const handleTouchEnd = () => handleDragEnd();

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      });
      document.addEventListener('touchend', handleTouchEnd);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [isDragging, dragStart, dragPosition]);

  // Click outside handler for definition popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        definitionRef.current &&
        !definitionRef.current.contains(event.target) &&
        !isDragging
      ) {
        // Track definition viewing time when closing by clicking outside
        if (definitionStartTime) {
          const timeSpent = Date.now() - definitionStartTime;
          setTotalDefinitionTime((prev) => prev + timeSpent);
          setDefinitionStartTime(null);
        }
        setShowDefinition(null);
      }
    };

    if (showDefinition) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showDefinition, isDragging, definitionStartTime]);

  // Close selection pronunciation popup when clicking outside
  useEffect(() => {
    if (!selectionPronunciation) return undefined;

    const handleClickOutside = (event) => {
      if (
        selectionPopupRef.current &&
        !selectionPopupRef.current.contains(event.target)
      ) {
        setSelectionPronunciation(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [selectionPronunciation]);

  const resolvePhoneticForWord = async (rawWord, knownPhonetic) => {
    const cleanKey = rawWord.trim().toLowerCase();
    if (knownPhonetic) {
      setWordPhonetics((prev) =>
        prev[cleanKey] ? prev : { ...prev, [cleanKey]: knownPhonetic }
      );
      return {
        word: rawWord,
        phonetic: knownPhonetic,
        audio_url: null,
      };
    }
    if (wordPhonetics[cleanKey]) {
      return {
        word: rawWord,
        phonetic: wordPhonetics[cleanKey],
        audio_url: null,
      };
    }

    const data = await fetchWordPronunciation(rawWord);
    if (data?.phonetic) {
      setWordPhonetics((prev) => ({ ...prev, [cleanKey]: data.phonetic }));
    }
    return data;
  };

  const handleArticleTextSelect = async () => {
    const selection = window.getSelection?.();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      return;
    }

    const selectedText = selection.toString().trim();
    if (!/^[A-Za-z][A-Za-z'-]{0,63}$/.test(selectedText)) {
      return;
    }

    const anchorNode = selection.anchorNode;
    if (
      !articleBodyRef.current ||
      !anchorNode ||
      !articleBodyRef.current.contains(anchorNode)
    ) {
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    if (!rect || (rect.width === 0 && rect.height === 0)) {
      return;
    }

    const requestId = selectionRequestIdRef.current + 1;
    selectionRequestIdRef.current = requestId;

    const vocabMatch = currentArticle?.words?.find((v) =>
      isWordVariation(selectedText.toLowerCase(), v.word.toLowerCase())
    );

    setShowDefinition(null);
    const popupHalfWidth = 90;
    const popupHeight = 88;
    const clampedX = Math.min(
      Math.max(rect.left + rect.width / 2, popupHalfWidth + 8),
      window.innerWidth - popupHalfWidth - 8
    );
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom - 72;
    const placeBelow = spaceBelow >= popupHeight || spaceBelow >= spaceAbove;
    const rawTop = placeBelow
      ? rect.bottom + 8
      : rect.top - popupHeight - 8;
    const clampedTop = Math.min(
      Math.max(rawTop, 12),
      window.innerHeight - popupHeight - 12
    );
    setSelectionPronunciation({
      word: vocabMatch?.word || selectedText,
      phonetic: vocabMatch?.phonetic || null,
      audio_url: null,
      x: clampedX,
      y: clampedTop,
      placeBelow,
      loading: !vocabMatch?.phonetic,
      error: null,
    });

    if (vocabMatch?.phonetic) {
      setWordPhonetics((prev) => ({
        ...prev,
        [vocabMatch.word.toLowerCase()]: vocabMatch.phonetic,
      }));
      return;
    }

    const data = await resolvePhoneticForWord(
      vocabMatch?.word || selectedText,
      vocabMatch?.phonetic
    );

    if (selectionRequestIdRef.current !== requestId) {
      return;
    }

    // Missing IPA / dictionary audio is fine: the speaker still uses browser TTS.
    // Only show an error when speech synthesis itself is unavailable.
    const canSpeak =
      typeof window !== 'undefined' && 'speechSynthesis' in window;
    if (!data || (!data.phonetic && !data.audio_url)) {
      setSelectionPronunciation((prev) =>
        prev
          ? {
              ...prev,
              loading: false,
              phonetic: null,
              audio_url: null,
              error: canSpeak ? null : 'Pronunciation unavailable',
            }
          : null
      );
      return;
    }

    setSelectionPronunciation((prev) =>
      prev
        ? {
            ...prev,
            word: data.word || prev.word,
            phonetic: data.phonetic || null,
            audio_url: data.audio_url || null,
            loading: false,
            error: null,
          }
        : null
    );
  };

  useEffect(() => {
    if (currentStep === 'exercise') {
      setExerciseStartTime(Date.now());
      setSelectedAnswer(null);
      setSelectedAnswers([]);
      setShowFeedback(false);
      if (exercises.length > 0 && exercises[exerciseIndex]) {
        const shuffled = [...exercises[exerciseIndex].options].sort(
          () => Math.random() - 0.5
        );
        setRandomizedOptions(shuffled);
      }
    }
  }, [currentStep, exerciseIndex, exercises]);

  const normalizeProgressIds = (ids) =>
    Array.isArray(ids) ? Array.from(new Set(ids.map(String))) : [];

  const getExerciseProgressKey = (exercise, index) =>
    String(exercise?.id ?? `${currentArticle?.id || 'article'}-${index}`);

  const handleNextStep = () => {
    if (currentStep === 'article') {
      const wpm = calculateArticleWPM();
      setArticleWPM(wpm);

      if (isRevisionMode) {
        trackVocabRevisionExercisesStarted({
          article_public_code: getGreArticlePublicRef(currentArticle),
        });
      }

      setCurrentStep('exercise');
      setExerciseIndex(0);
      setSelectedAnswer(null);
      setSelectedAnswers([]);
      setShowFeedback(false);
    } else {
      if (exerciseIndex >= exercises.length - 1) {
        if (currentArticle?.id) {
          markArticleAsReadExternal(currentArticle.id);
        }
        if (isRevisionMode) {
          trackVocabRevisionCompleted({
            article_public_code: getGreArticlePublicRef(currentArticle),
          });
          navigate('/exams/gre/verbal/vocabulary/history');
          return;
        }
        navigate('/exams/gre/', {
          state: { activeCategory: 'vocabularymastery' },
        });
      } else {
        setExerciseIndex((prev) => prev + 1);
        setSelectedAnswer(null);
        setSelectedAnswers([]);
        setShowFeedback(false);
      }
    }
  };

  const handleAnswerSelect = async (answer) => {
    if (showFeedback) return;

    const exercise = exercises[exerciseIndex];

    if (exercise._isMultiSelect) {
      setSelectedAnswers((prev) =>
        prev.includes(answer)
          ? prev.filter((a) => a !== answer)
          : [...prev, answer]
      );
      return;
    }

    const isCorrect = answer === exercise.correct_answer;
    const timeTaken = exerciseStartTime
      ? Math.round((Date.now() - exerciseStartTime) / 1000)
      : 0;

    setSelectedAnswer(answer);
    setShowFeedback(true);

    if (!completedExercises.includes(exerciseIndex)) {
      setCompletedExercises([...completedExercises, exerciseIndex]);
    }

    const currentProgress = getArticleProgressExternal(currentArticle.id);
    const completedExerciseIds = normalizeProgressIds(
      currentProgress.completedExerciseIds
    );
    const masteredWordExerciseIds = normalizeProgressIds(
      currentProgress.masteredWordExerciseIds
    );
    const exerciseKey = getExerciseProgressKey(exercise, exerciseIndex);
    const nextCompletedExerciseIds = completedExerciseIds.includes(exerciseKey)
      ? completedExerciseIds
      : [...completedExerciseIds, exerciseKey];
    const nextMasteredWordExerciseIds =
      exercise._exerciseType === 'word' && isCorrect
        ? masteredWordExerciseIds.includes(exerciseKey)
          ? masteredWordExerciseIds
          : [...masteredWordExerciseIds, exerciseKey]
        : masteredWordExerciseIds;

    updateArticleProgressExternal(currentArticle.id, {
      ...currentProgress,
      totalExercises: exercises.length,
      totalWords: currentArticle.words?.length || 0,
      completedExerciseIds: nextCompletedExerciseIds,
      masteredWordExerciseIds: nextMasteredWordExerciseIds,
      exercisesCompleted: Math.min(nextCompletedExerciseIds.length, exercises.length),
      wordsMastered: Math.min(
        nextMasteredWordExerciseIds.length,
        currentArticle.words?.length || nextMasteredWordExerciseIds.length
      ),
    });

    try {
      const exerciseType =
        exercise._exerciseType === 'rc' ? 'article_rc' : 'article_word';
      const userAnswer =
        exercise._exerciseType === 'rc'
          ? String(exercise.options.indexOf(answer))
          : answer;
      await recordExerciseAttempt(
        exercise.id,
        userAnswer,
        isCorrect,
        timeTaken,
        currentArticle.id,
        exerciseType
      );

      if (isCorrect) {
        clearProgressCache();
      }
    } catch (error) {
      console.error('Error recording exercise data:', error);
    }
  };

  const handleMultiSelectSubmit = async () => {
    if (showFeedback || selectedAnswers.length === 0) return;

    const exercise = exercises[exerciseIndex];
    const correctTexts = new Set(exercise._correctAnswerTexts || []);
    const isCorrect =
      selectedAnswers.length === correctTexts.size &&
      selectedAnswers.every((a) => correctTexts.has(a));
    const timeTaken = exerciseStartTime
      ? Math.round((Date.now() - exerciseStartTime) / 1000)
      : 0;

    setShowFeedback(true);

    if (!completedExercises.includes(exerciseIndex)) {
      setCompletedExercises([...completedExercises, exerciseIndex]);
    }

    const currentProgress = getArticleProgressExternal(currentArticle.id);
    const completedExerciseIds = normalizeProgressIds(
      currentProgress.completedExerciseIds
    );
    const exerciseKey = getExerciseProgressKey(exercise, exerciseIndex);
    const nextCompletedExerciseIds = completedExerciseIds.includes(exerciseKey)
      ? completedExerciseIds
      : [...completedExerciseIds, exerciseKey];

    updateArticleProgressExternal(currentArticle.id, {
      ...currentProgress,
      totalExercises: exercises.length,
      totalWords: currentArticle.words?.length || 0,
      completedExerciseIds: nextCompletedExerciseIds,
      exercisesCompleted: Math.min(nextCompletedExerciseIds.length, exercises.length),
    });

    try {
      const selectedIndices = selectedAnswers.map((a) =>
        exercise.options.indexOf(a)
      );
      await recordExerciseAttempt(
        exercise.id,
        JSON.stringify(selectedIndices),
        isCorrect,
        timeTaken,
        currentArticle.id,
        'article_rc'
      );
      if (isCorrect) clearProgressCache();
    } catch (error) {
      console.error('Error recording exercise data:', error);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '1rem',
          minHeight: '100vh',
        }}
      >
        <style>{`
          @keyframes vocabularyArticleSkeletonShimmer {
            0% { background-position: 100% 0; }
            100% { background-position: -100% 0; }
          }
        `}</style>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
          }}
        >
          <div
            style={{
              height: '48px',
              width: '220px',
              borderRadius: '10px',
              background:
                'linear-gradient(90deg, var(--liwaza-surface-container) 25%, var(--liwaza-surface-container-highest, var(--liwaza-outline-variant)) 50%, var(--liwaza-surface-container) 75%)',
              backgroundSize: '200% 100%',
              animation: 'vocabularyArticleSkeletonShimmer 1.4s ease-in-out infinite',
            }}
          />
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              border: '1px solid var(--liwaza-outline-variant)',
              borderRadius: '12px',
              padding: '1.5rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
            }}
          >
            <div
              style={{
                height: '36px',
                width: '55%',
                borderRadius: '10px',
                background:
                  'linear-gradient(90deg, var(--liwaza-surface) 25%, var(--liwaza-surface-container-highest, var(--liwaza-outline-variant)) 50%, var(--liwaza-surface) 75%)',
                backgroundSize: '200% 100%',
                animation:
                  'vocabularyArticleSkeletonShimmer 1.4s ease-in-out infinite',
              }}
            />
            {[...Array(7)].map((_, index) => (
              <div
                key={index}
                style={{
                  height: '18px',
                  width:
                    index === 6 ? '68%' : index % 3 === 0 ? '100%' : '92%',
                  borderRadius: '999px',
                  background:
                    'linear-gradient(90deg, var(--liwaza-surface) 25%, var(--liwaza-surface-container-highest, var(--liwaza-outline-variant)) 50%, var(--liwaza-surface) 75%)',
                  backgroundSize: '200% 100%',
                  animation:
                    'vocabularyArticleSkeletonShimmer 1.4s ease-in-out infinite',
                }}
              />
            ))}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
              gap: '1.5rem',
            }}
          >
            {[...Array(2)].map((_, cardIndex) => (
              <div
                key={cardIndex}
                style={{
                  background: 'var(--liwaza-surface-container)',
                  border: '1px solid var(--liwaza-outline-variant)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <div
                  style={{
                    height: '28px',
                    width: cardIndex === 0 ? '40%' : '50%',
                    borderRadius: '8px',
                    background:
                      'linear-gradient(90deg, var(--liwaza-surface) 25%, var(--liwaza-surface-container-highest, var(--liwaza-outline-variant)) 50%, var(--liwaza-surface) 75%)',
                    backgroundSize: '200% 100%',
                    animation:
                      'vocabularyArticleSkeletonShimmer 1.4s ease-in-out infinite',
                  }}
                />
                {[...Array(cardIndex === 0 ? 6 : 5)].map((__, rowIndex) => (
                  <div
                    key={rowIndex}
                    style={{
                      height: cardIndex === 0 ? '16px' : '48px',
                      width: '100%',
                      borderRadius: '10px',
                      background:
                        'linear-gradient(90deg, var(--liwaza-surface) 25%, var(--liwaza-surface-container-highest, var(--liwaza-outline-variant)) 50%, var(--liwaza-surface) 75%)',
                      backgroundSize: '200% 100%',
                      animation:
                        'vocabularyArticleSkeletonShimmer 1.4s ease-in-out infinite',
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-error)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <h2>Error Loading Article</h2>
        <p>{getErrorMessage(error, 'Failed to load article.')}</p>
        <button
          onClick={() =>
            articleAccessBlocked
              ? navigate(GRE_ARTICLE_ACCESS_REDIRECT_PATH, { replace: true })
              : navigate('/exams/gre/', {
                  state: { activeCategory: 'vocabularymastery' },
                })
          }
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          {articleAccessBlocked
            ? 'View My Vocabulary Articles'
            : 'Back to Vocabulary Mastery'}
        </button>
      </div>
    );
  }

  if (sessionExpired) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          color: 'var(--liwaza-on-surface)',
          textAlign: 'center',
          padding: '2rem',
        }}
      >
        <h2>Session Expired</h2>
        <p>Your session has expired. Redirecting you to login...</p>
        <button
          onClick={() => {
            window.location.href = '/login';
          }}
          style={{
            background: 'var(--liwaza-primary)',
            color: 'white',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            cursor: 'pointer',
            marginTop: '1rem',
          }}
        >
          Go to Login
        </button>
      </div>
    );
  }

  if (!currentArticle) {
    return <div>Article not found</div>;
  }

  const articleContent = currentArticle?.content ?? '';
  const articleWords = articleContent.trim()
    ? articleContent.trim().split(/\s+/)
    : [];

  const renderArticle = () => (
    <div
      className="reading-container"
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '1rem',
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--liwaza-surface)',
          padding: '1rem',
          zIndex: 10,
          borderBottom: '1px solid var(--liwaza-outline-variant)',
          marginBottom: '2rem',
        }}
      >
        <button
          onClick={() =>
            navigate(
              isRevisionMode
                ? '/exams/gre/verbal/vocabulary/history'
                : '/exams/gre/',
              isRevisionMode
                ? undefined
                : { state: { activeCategory: 'vocabularymastery' } }
            )
          }
          style={{
            background: 'none',
            border: 'none',
            color: 'var(--liwaza-primary)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            cursor: 'pointer',
            fontSize: '1rem',
          }}
        >
          <FiArrowLeft />{' '}
          {isRevisionMode ? 'Back to Article History' : 'Back to Vocabulary Mastery'}
        </button>
        {isRevisionMode && (
          <div
            className="vocabulary_status_badge completed"
            style={{ marginTop: '0.75rem', display: 'inline-flex' }}
          >
            Revising · previously completed
          </div>
        )}
      </div>

      {/* Content */}
      <div
        style={{ flex: 1, maxWidth: '65ch', margin: '0 auto', width: '100%' }}
      >
        <h1
          style={{
            fontSize: 'clamp(1.5rem, 5vw, 2rem)',
            color: 'var(--liwaza-on-surface)',
            marginBottom: '1.5rem',
            lineHeight: 1.3,
          }}
        >
          {currentArticle.title}
        </h1>

        {currentArticle.excerpt && (
          <p
            style={{
              fontSize: '1.1rem',
              color: 'var(--liwaza-on-surface-variant)',
              fontStyle: 'italic',
              marginBottom: '1.5rem',
              lineHeight: 1.5,
            }}
          >
            {currentArticle.excerpt}
          </p>
        )}

        <div
          style={{
            padding: '1rem',
            background: 'var(--liwaza-primary-container)',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            color: 'var(--liwaza-on-primary-container)',
            fontSize: '0.9rem',
            lineHeight: 1.5,
          }}
        >
          <strong>Note:</strong> Words in <strong>bold</strong> are important
          GRE vocabulary words. Click them for definitions, or select any word
          to hear / see its English pronunciation.
        </div>

        <div
          ref={articleBodyRef}
          onMouseUp={handleArticleTextSelect}
          onTouchEnd={handleArticleTextSelect}
          style={{
            color: 'var(--liwaza-on-surface)',
            fontSize: 'clamp(1rem, 2.5vw, 1.1rem)',
            lineHeight: 1.8,
            marginBottom: '2rem',
            textAlign: 'justify',
          }}
        >
          {articleContent.split(/(\s+)/).map((word, index) => {
            // Skip whitespace-only tokens
            if (/^\s+$/.test(word)) {
              return word;
            }
            if (!word) return null;

            // Clean the word by removing punctuation for matching
            const cleanWord = word.replace(/[^\w]/g, '').toLowerCase();
            // Check for exact match first, then check for word variations
            const vocabWord = currentArticle.words?.find((v) =>
              isWordVariation(cleanWord, v.word.toLowerCase())
            );

            return (
              <React.Fragment key={index}>
                <span
                  style={{
                    backgroundColor: vocabWord
                      ? 'var(--liwaza-primary-container)'
                      : 'transparent',
                    color: vocabWord
                      ? 'var(--liwaza-on-primary-container)'
                      : 'inherit',
                    padding: vocabWord ? '0.1rem 0.2rem' : '0',
                    borderRadius: '4px',
                    cursor: vocabWord ? 'pointer' : 'inherit',
                    position: 'relative',
                    fontWeight: vocabWord ? 700 : 'normal',
                    margin: vocabWord ? '0 1px' : '0',
                  }}
                  onClick={(e) => {
                    if (vocabWord) {
                      const uniqueId = `${vocabWord.word}-${index}`;
                      setSelectionPronunciation(null);

                      // Track definition viewing time
                      if (showDefinition === uniqueId) {
                        // Closing definition - add time spent to total
                        if (definitionStartTime) {
                          const timeSpent = Date.now() - definitionStartTime;
                          setTotalDefinitionTime((prev) => prev + timeSpent);
                          setDefinitionStartTime(null);
                        }
                        setShowDefinition(null);
                      } else {
                        // Opening definition - close any existing definition first
                        if (showDefinition && definitionStartTime) {
                          const timeSpent = Date.now() - definitionStartTime;
                          setTotalDefinitionTime((prev) => prev + timeSpent);
                        }
                        const wordRect = e.currentTarget.getBoundingClientRect();
                        // Keep the card readable above the sticky footer: if the
                        // word is not low enough for a tall popup above it, open below.
                        const placeBelow =
                          wordRect.top <
                          Math.min(window.innerHeight * 0.55, 320);
                        const available = placeBelow
                          ? window.innerHeight - wordRect.bottom - 80
                          : wordRect.top - 16;
                        setDefinitionPlacement(placeBelow ? 'below' : 'above');
                        setDefinitionMaxHeight(
                          Math.max(180, Math.min(420, available))
                        );
                        // Start tracking time for new definition
                        setDefinitionStartTime(Date.now());
                        setShowDefinition(uniqueId);
                        if (
                          !vocabWord.phonetic &&
                          !wordPhonetics[vocabWord.word.toLowerCase()]
                        ) {
                          resolvePhoneticForWord(vocabWord.word, null);
                        }
                      }
                    }
                  }}
                >
                  {word}
                  {showDefinition === `${vocabWord?.word}-${index}` && (
                    <>
                      {isMobile && (
                        <div
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            zIndex: 999,
                          }}
                        />
                      )}
                      <div
                        ref={definitionRef}
                        onMouseDown={handleDragStart}
                        onTouchStart={handleDragStart}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: isMobile ? 'fixed' : 'absolute',
                          ...(isMobile
                            ? {
                                top: '50%',
                                left: '50%',
                                transform: `translate(calc(-50% + ${dragPosition.x}px), calc(-50% + ${dragPosition.y}px))`,
                                width: 'calc(100vw - 2rem)',
                                maxWidth: '350px',
                                maxHeight: 'min(70vh, 520px)',
                                overflowY: 'auto',
                              }
                            : definitionPlacement === 'below'
                              ? {
                                  top: '100%',
                                  left: '50%',
                                  transform: `translateX(calc(-50% + ${dragPosition.x}px)) translateY(${dragPosition.y}px)`,
                                  width: 'max-content',
                                  maxWidth: '350px',
                                  marginTop: '0.5rem',
                                  maxHeight: `${definitionMaxHeight}px`,
                                  overflowY: 'auto',
                                }
                              : {
                                  bottom: '100%',
                                  left: '50%',
                                  transform: `translateX(calc(-50% + ${dragPosition.x}px)) translateY(${dragPosition.y}px)`,
                                  width: 'max-content',
                                  maxWidth: '350px',
                                  marginBottom: '0.5rem',
                                  maxHeight: `${definitionMaxHeight}px`,
                                  overflowY: 'auto',
                                }),
                          background: 'var(--liwaza-primary-container)',
                          padding: '0',
                          borderRadius: '12px',
                          boxShadow: isMobile
                            ? '0 8px 32px rgba(0,0,0,0.3)'
                            : '0 4px 16px rgba(0,0,0,0.15)',
                          zIndex: 1000,
                          color: 'var(--liwaza-on-surface)',
                          fontSize: '0.9rem',
                          border: '1px solid var(--liwaza-outline-variant)',
                          cursor: isDragging ? 'grabbing' : 'grab',
                          userSelect: 'none',
                        }}
                      >
                        {/* Drag Handle */}
                        <div
                          style={{
                            background: 'var(--liwaza-primary-container)',
                            padding: '0.75rem 1rem 0.5rem',
                            borderRadius: '12px 12px 0 0',
                            borderBottom:
                              '1px solid var(--liwaza-outline-variant)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'grab',
                          }}
                        >
                          <div
                            style={{
                              width: '40px',
                              height: '4px',
                              background: 'var(--liwaza-on-primary-container)',
                              borderRadius: '2px',
                              opacity: 0.5,
                            }}
                          />
                        </div>

                        {/* Content */}
                        <div
                          style={{ padding: '1rem' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div style={{ marginBottom: '0.5rem' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                marginBottom: '0.25rem',
                              }}
                            >
                              <strong
                                style={{
                                  fontSize: '1rem',
                                  color: 'var(--liwaza-primary)',
                                  flex: 1,
                                }}
                              >
                                {vocabWord.word}
                              </strong>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  pronounceWord(vocabWord.word);
                                }}
                                style={{
                                  background: 'var(--liwaza-primary)',
                                  color: 'var(--liwaza-on-primary)',
                                  border: '1px solid var(--liwaza-primary)',
                                  borderRadius: '6px',
                                  padding: '0.25rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  minWidth: '28px',
                                  minHeight: '28px',
                                  transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                  e.target.style.background =
                                    'var(--liwaza-primary)';
                                  e.target.style.color =
                                    'var(--liwaza-on-primary)';
                                  e.target.style.borderColor =
                                    'var(--liwaza-primary)';
                                  e.target.style.transform = 'translateY(-1px)';
                                  e.target.style.boxShadow =
                                    '0 2px 8px rgba(0,0,0,0.15)';
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.background =
                                    'var(--liwaza-primary)';
                                  e.target.style.color =
                                    'var(--liwaza-on-primary)';
                                  e.target.style.borderColor =
                                    'var(--liwaza-primary)';
                                  e.target.style.transform = 'translateY(0)';
                                  e.target.style.boxShadow = 'none';
                                }}
                                title="Listen to pronunciation"
                              >
                                <FiVolume2 size={14} />
                              </button>

                              {vocabWord.difficulty_level && (
                                <span
                                  style={{
                                    fontSize: '0.7rem',
                                    background:
                                      vocabWord.difficulty_level === 'easy'
                                        ? 'var(--liwaza-success-container)'
                                        : vocabWord.difficulty_level ===
                                            'medium'
                                          ? 'var(--liwaza-warning-container)'
                                          : 'var(--liwaza-error-container)',
                                    color:
                                      vocabWord.difficulty_level === 'easy'
                                        ? 'var(--liwaza-on-success-container)'
                                        : vocabWord.difficulty_level ===
                                            'medium'
                                          ? 'var(--liwaza-on-warning-container)'
                                          : 'var(--liwaza-on-error-container)',
                                    padding: '0.2rem 0.4rem',
                                    borderRadius: '4px',
                                    textTransform: 'uppercase',
                                    fontWeight: '600',
                                  }}
                                >
                                  {vocabWord.difficulty_level}
                                </span>
                              )}
                            </div>
                            {(vocabWord.phonetic ||
                              wordPhonetics[vocabWord.word.toLowerCase()]) && (
                              <div
                                style={{
                                  fontSize: '0.9rem',
                                  color: 'var(--liwaza-on-surface-variant)',
                                  fontFamily:
                                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                }}
                              >
                                {vocabWord.phonetic ||
                                  wordPhonetics[vocabWord.word.toLowerCase()]}
                              </div>
                            )}
                          </div>

                          {vocabWord.definition_en && (
                            <div style={{ marginBottom: '0.75rem' }}>
                              <div
                                style={{
                                  fontWeight: '600',
                                  marginBottom: '0.25rem',
                                  fontSize: '0.8rem',
                                  color: 'var(--liwaza-primary)',
                                }}
                              >
                                EN definition:
                              </div>
                              <div style={{ lineHeight: 1.4 }}>
                                {vocabWord.definition_en}
                              </div>
                            </div>
                          )}

                          {vocabWord.definition_fr && (
                            <div style={{ marginBottom: '0.75rem' }}>
                              <div
                                style={{
                                  fontWeight: '600',
                                  marginBottom: '0.25rem',
                                  fontSize: '0.8rem',
                                  color: 'var(--liwaza-primary)',
                                }}
                              >
                                FR definition:
                              </div>
                              <div
                                style={{ lineHeight: 1.4, fontStyle: 'italic' }}
                              >
                                {vocabWord.definition_fr}
                              </div>
                            </div>
                          )}

                          {vocabWord.example_sentence_en && (
                            <div style={{ marginBottom: '0.5rem' }}>
                              <div
                                style={{
                                  fontWeight: '600',
                                  marginBottom: '0.25rem',
                                  fontSize: '0.8rem',
                                  color: 'var(--liwaza-primary)',
                                }}
                              >
                                EN example:
                              </div>
                              <div
                                style={{
                                  lineHeight: 1.4,
                                  fontStyle: 'italic',
                                  background: 'var(--liwaza-surface-container)',
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  borderLeft: '2px solid var(--liwaza-primary)',
                                }}
                              >
                                "{vocabWord.example_sentence_en}"
                              </div>
                            </div>
                          )}

                          {vocabWord.example_sentence_fr && (
                            <div>
                              <div
                                style={{
                                  fontWeight: '600',
                                  marginBottom: '0.25rem',
                                  fontSize: '0.8rem',
                                  color: 'var(--liwaza-primary)',
                                }}
                              >
                                FR example:
                              </div>
                              <div
                                style={{
                                  lineHeight: 1.4,
                                  fontStyle: 'italic',
                                  background: 'var(--liwaza-surface-container)',
                                  padding: '0.5rem',
                                  borderRadius: '6px',
                                  borderLeft: '2px solid var(--liwaza-primary)',
                                }}
                              >
                                "{vocabWord.example_sentence_fr}"
                              </div>
                            </div>
                          )}

                          {/* Add to Flashcards Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addToFlashcards(vocabWord);
                            }}
                            disabled={addedToFlashcards.includes(
                              vocabWord.word
                            )}
                            style={{
                              width: '70%',
                              marginTop: '1rem',
                              background: addedToFlashcards.includes(
                                vocabWord.word
                              )
                                ? 'var(--liwaza-surface-variant)'
                                : 'var(--liwaza-primary)',
                              color: addedToFlashcards.includes(vocabWord.word)
                                ? 'var(--liwaza-on-surface-variant)'
                                : 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: addedToFlashcards.includes(vocabWord.word)
                                ? 'not-allowed'
                                : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '0.5rem',
                              fontSize: '0.9rem',
                              fontWeight: 600,
                              transition: 'all 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              if (!addedToFlashcards.includes(vocabWord.word)) {
                                e.target.style.background =
                                  'var(--liwaza-primary-hover)';
                                e.target.style.transform = 'translateY(-2px)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!addedToFlashcards.includes(vocabWord.word)) {
                                e.target.style.background =
                                  'var(--liwaza-primary)';
                                e.target.style.transform = 'translateY(0)';
                              }
                            }}
                          >
                            <FiBookmark size={16} />
                            {addedToFlashcards.includes(vocabWord.word)
                              ? 'Already in Flashcards'
                              : 'Add to Flashcards'}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </span>
              </React.Fragment>
            );
          })}
        </div>

        {selectionPronunciation && (
          <div
            ref={selectionPopupRef}
            style={{
              position: 'fixed',
              left: Math.min(
                Math.max(selectionPronunciation.x, 16),
                window.innerWidth - 16
              ),
              top: selectionPronunciation.y,
              transform: 'translateX(-50%)',
              zIndex: 1100,
              background: 'var(--liwaza-surface)',
              color: 'var(--liwaza-on-surface)',
              border: '1px solid var(--liwaza-outline-variant)',
              borderRadius: '10px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
              padding: '0.65rem 0.8rem',
              minWidth: '160px',
              maxWidth: '260px',
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '0.25rem',
              }}
            >
              <strong style={{ flex: 1, color: 'var(--liwaza-primary)' }}>
                {selectionPronunciation.word}
              </strong>
              <button
                type="button"
                onClick={() =>
                  pronounceWord(
                    selectionPronunciation.word,
                    selectionPronunciation.audio_url
                  )
                }
                style={{
                  background: 'var(--liwaza-primary)',
                  color: 'var(--liwaza-on-primary)',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '0.25rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '28px',
                  minHeight: '28px',
                }}
                title="Listen to pronunciation"
              >
                <FiVolume2 size={14} />
              </button>
            </div>
            {selectionPronunciation.loading && (
              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                Looking up pronunciation…
              </div>
            )}
            {!selectionPronunciation.loading &&
              selectionPronunciation.phonetic && (
                <div
                  style={{
                    fontSize: '0.95rem',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    color: 'var(--liwaza-on-surface-variant)',
                  }}
                >
                  {selectionPronunciation.phonetic}
                </div>
              )}
            {!selectionPronunciation.loading &&
              selectionPronunciation.error && (
                <div style={{ fontSize: '0.85rem', color: 'var(--liwaza-error)' }}>
                  {selectionPronunciation.error}
                </div>
              )}
            {!selectionPronunciation.loading &&
              !selectionPronunciation.phonetic &&
              !selectionPronunciation.error && (
                <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                  Tap the speaker to hear it
                </div>
              )}
          </div>
        )}

        {/* Get Started With Component */}
        <div style={{ margin: '3rem 0 2rem 0' }}>
          <GetStartedWith
            title="Need help understanding this article?"
            spaceName="Verbal vault"
            humanCoachTitle="Talk to a GRE coach"
            liwazaAITitle="Ask AI"
            aifirstmessage={`I need help understanding the article "${currentArticle?.title}"`}
            aicontext={
              'understanding an article with title: ' +
              currentArticle?.title +
              ' and content: ' +
              currentArticle?.content
            }
            communityTitle="Ask other students"
            showHumanCoach={true}
            showLiwazaAI={true}
            showCommunity={true}
            style={{ border: 'none' }}
          />
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--liwaza-surface)',
          padding: '1rem',
          borderTop: '1px solid var(--liwaza-outline-variant)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: 'var(--liwaza-on-surface-variant)',
            fontSize: '0.9rem',
          }}
        >
          <FiBookOpen />
          <span>
            {currentArticle.words?.length || 0} vocabulary words to master
          </span>
        </div>
        <button
          onClick={handleNextStep}
          disabled={exercises.length === 0}
          style={{
            background:
              exercises.length > 0
                ? 'var(--liwaza-primary)'
                : 'var(--liwaza-surface-variant)',
            color:
              exercises.length > 0
                ? 'white'
                : 'var(--liwaza-on-surface-variant)',
            border: 'none',
            padding: '0.75rem 1.5rem',
            borderRadius: '8px',
            fontSize: '1rem',
            cursor: exercises.length > 0 ? 'pointer' : 'not-allowed',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          {exercises.length > 0
            ? isRevisionMode
              ? 'Start revision'
              : 'Start Exercises'
            : 'No Exercises Available'}{' '}
          <FiArrowRight />
        </button>
      </div>
    </div>
  );

  const renderExercise = () => {
    if (exercises.length === 0) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            color: 'var(--liwaza-on-surface-variant)',
            textAlign: 'center',
          }}
        >
          <h2>No Exercises Available</h2>
          <p>There are no exercises for this article yet.</p>
          <button
            onClick={() => navigate('/gre/vocabulary-mastery')}
            style={{
              background: 'var(--liwaza-primary)',
              color: 'white',
              border: 'none',
              padding: '0.75rem 1.5rem',
              borderRadius: '8px',
              cursor: 'pointer',
              marginTop: '1rem',
            }}
          >
            Back to Vocabulary Mastery
          </button>
        </div>
      );
    }

    const exercise = exercises[exerciseIndex];
    const isRc = exercise._exerciseType === 'rc';
    const isMulti = exercise._isMultiSelect;
    const rcTypeLabel = isRc
      ? RC_TYPE_LABELS[exercise.rc_type] || exercise.rc_type
      : null;

    let isCorrect;
    if (isMulti) {
      const correctTexts = new Set(exercise._correctAnswerTexts || []);
      isCorrect =
        selectedAnswers.length === correctTexts.size &&
        selectedAnswers.every((a) => correctTexts.has(a));
    } else {
      isCorrect = selectedAnswer === exercise.correct_answer;
    }

    const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

    return (
      <div
        className="exercise-container"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '1rem',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {toast.show && (
          <div
            className={`toast-notification ${toast.success ? 'success' : 'error'}`}
            style={{
              position: 'fixed',
              top: '24px',
              right: '24px',
              zIndex: 9999,
            }}
          >
            <div className="toast-icon">{toast.success ? '✓' : '⚠'}</div>
            {toast.message}
          </div>
        )}
        {/* Header */}
        <div
          style={{
            position: 'sticky',
            top: 0,
            background: 'var(--liwaza-surface)',
            padding: '1rem',
            zIndex: 10,
            borderBottom: '1px solid var(--liwaza-outline-variant)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
          }}
        >
          <button
            onClick={() => setCurrentStep('article')}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--liwaza-primary)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              cursor: 'pointer',
              fontSize: '1rem',
            }}
          >
            <FiArrowLeft /> Back to Article
          </button>
          <div
            style={{
              color: 'var(--liwaza-on-surface-variant)',
              fontSize: '0.9rem',
            }}
          >
            Exercise {exerciseIndex + 1} of {exercises.length}
          </div>
        </div>

        {/* Two-column layout */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns:
              'repeat(auto-fit, minmax(min(100%, 500px), 1fr))',
            gap: '2rem',
            alignItems: 'start',
          }}
        >
          {/* Article column */}
          <div
            style={{
              background: 'var(--liwaza-surface-container)',
              padding: 'clamp(1rem, 3vw, 1.5rem)',
              borderRadius: '12px',
              border: '1px solid var(--liwaza-outline-variant)',
            }}
          >
            <h3
              style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                color: 'var(--liwaza-on-surface)',
                marginBottom: '1rem',
              }}
            >
              Article Reference
            </h3>
            <div
              style={{
                color: 'var(--liwaza-on-surface)',
                fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                lineHeight: 1.8,
                maxHeight: '400px',
                overflowY: 'auto',
                padding: '0.5rem',
              }}
            >
              {articleWords.map((word, index) => {
                const cleanWord = word.replace(/[^\w]/g, '').toLowerCase();
                const vocabWord = currentArticle.words?.find((v) =>
                  isWordVariation(cleanWord, v.word.toLowerCase())
                );

                return (
                  <React.Fragment key={index}>
                    <span
                      style={{
                        backgroundColor: vocabWord
                          ? 'var(--liwaza-primary-container)'
                          : 'transparent',
                        color: vocabWord
                          ? 'var(--liwaza-on-primary-container)'
                          : 'inherit',
                        padding: vocabWord ? '0.2rem 0.4rem' : '0',
                        borderRadius: '4px',
                        cursor: vocabWord ? 'pointer' : 'inherit',
                        position: 'relative',
                        display: 'inline-block',
                        fontWeight: vocabWord ? 700 : 'normal',
                        margin: vocabWord ? '0 1px' : '0',
                      }}
                    >
                      {word}
                    </span>
                    {index < articleWords.length - 1 && ' '}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Exercise column */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1.5rem',
            }}
          >
            <div
              style={{
                background: 'var(--liwaza-surface-container)',
                padding: 'clamp(1rem, 3vw, 1.5rem)',
                borderRadius: '12px',
                border: '1px solid var(--liwaza-outline-variant)',
              }}
            >
              {isRc && rcTypeLabel && (
                <div
                  style={{
                    display: 'inline-block',
                    padding: '0.25rem 0.75rem',
                    borderRadius: '16px',
                    background:
                      'var(--liwaza-tertiary-container, var(--liwaza-primary-container))',
                    color:
                      'var(--liwaza-on-tertiary-container, var(--liwaza-on-primary-container))',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    marginBottom: '0.75rem',
                  }}
                >
                  Reading Comprehension &mdash; {rcTypeLabel}
                </div>
              )}

              <h3
                style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                  color: 'var(--liwaza-on-surface)',
                  marginBottom: '1rem',
                }}
              >
                Question {exerciseIndex + 1} of {exercises.length}
              </h3>

              {isMulti && !showFeedback && (
                <p
                  style={{
                    fontSize: '0.85rem',
                    color: 'var(--liwaza-on-surface-variant)',
                    marginBottom: '0.5rem',
                    fontStyle: 'italic',
                  }}
                >
                  Select all that apply
                </p>
              )}

              <p
                style={{
                  color: 'var(--liwaza-on-surface)',
                  fontSize: 'clamp(0.9rem, 2vw, 1rem)',
                  lineHeight: 1.6,
                  marginBottom: '1.5rem',
                }}
              >
                {exercise.question}
              </p>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                }}
              >
                {randomizedOptions.map((option, index) => {
                  const isSelected = isMulti
                    ? selectedAnswers.includes(option)
                    : selectedAnswer === option;
                  const isCorrectAnswer = isMulti
                    ? (exercise._correctAnswerTexts || []).includes(option)
                    : option === exercise.correct_answer;

                  let buttonBackground,
                    buttonColor,
                    buttonBorder,
                    letterBackground,
                    letterColor;

                  if (showFeedback && isSelected) {
                    if (isCorrectAnswer) {
                      buttonBackground = 'var(--liwaza-success-container)';
                      buttonColor = 'var(--liwaza-on-success-container)';
                      buttonBorder = 'var(--liwaza-success)';
                      letterBackground = 'var(--liwaza-success-container)';
                      letterColor = 'var(--liwaza-on-surface)';
                    } else {
                      buttonBackground = 'var(--liwaza-error-container)';
                      buttonColor = 'var(--liwaza-error)';
                      buttonBorder = 'var(--liwaza-error)';
                      letterBackground = 'var(--liwaza-error-container)';
                      letterColor = 'var(--liwaza-on-surface)';
                    }
                  } else if (showFeedback && isCorrectAnswer) {
                    buttonBackground = 'var(--liwaza-success-container)';
                    buttonColor = 'var(--liwaza-on-success-container)';
                    buttonBorder = 'var(--liwaza-success)';
                    letterBackground = 'var(--liwaza-success-container)';
                    letterColor = 'var(--liwaza-on-surface)';
                  } else if (isSelected && !showFeedback) {
                    buttonBackground = 'var(--liwaza-primary-container)';
                    buttonColor = 'var(--liwaza-on-primary-container)';
                    buttonBorder = 'var(--liwaza-primary)';
                    letterBackground = 'var(--liwaza-primary-container)';
                    letterColor = 'var(--liwaza-on-surface)';
                  } else {
                    buttonBackground = 'var(--liwaza-surface)';
                    buttonColor = 'var(--liwaza-on-surface)';
                    buttonBorder = 'var(--liwaza-outline-variant)';
                    letterBackground = 'var(--liwaza-surface)';
                    letterColor = 'var(--liwaza-on-surface)';
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(option)}
                      disabled={showFeedback}
                      style={{
                        background: buttonBackground,
                        color: buttonColor,
                        border: `1px solid ${buttonBorder}`,
                        padding: '1rem',
                        borderRadius: '8px',
                        textAlign: 'left',
                        cursor: showFeedback ? 'default' : 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '1rem',
                      }}
                      onMouseEnter={(e) => {
                        if (!showFeedback && !isSelected) {
                          e.target.style.background =
                            'var(--liwaza-surface-container-highest)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!showFeedback && !isSelected) {
                          e.target.style.background = buttonBackground;
                        }
                      }}
                    >
                      {isMulti ? (
                        <span
                          style={{
                            width: '22px',
                            height: '22px',
                            borderRadius: '4px',
                            border: `2px solid ${
                              isSelected && !showFeedback
                                ? 'var(--liwaza-primary)'
                                : showFeedback && isCorrectAnswer
                                  ? 'var(--liwaza-success)'
                                  : showFeedback &&
                                      isSelected &&
                                      !isCorrectAnswer
                                    ? 'var(--liwaza-error)'
                                    : 'var(--liwaza-outline)'
                            }`,
                            background:
                              isSelected && !showFeedback
                                ? 'var(--liwaza-primary)'
                                : showFeedback && isSelected && isCorrectAnswer
                                  ? 'var(--liwaza-success)'
                                  : showFeedback &&
                                      isSelected &&
                                      !isCorrectAnswer
                                    ? 'var(--liwaza-error)'
                                    : 'transparent',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: '1px',
                          }}
                        >
                          {isSelected && (
                            <FiCheck
                              style={{
                                color: 'white',
                                strokeWidth: 3,
                                width: 14,
                                height: 14,
                              }}
                            />
                          )}
                        </span>
                      ) : (
                        <span
                          style={{
                            color: 'var(--liwaza-on-surface)',
                            backgroundColor: 'none',
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            flexShrink: 0,
                          }}
                        >
                          {optionLetters[index]}
                        </span>
                      )}
                      <span style={{ flex: 1, backgroundColor: 'none' }}>
                        {option}
                      </span>
                      {showFeedback && isCorrectAnswer && (
                        <FiCheck
                          style={{
                            color: 'var(--liwaza-success)',
                            strokeWidth: 3,
                            flexShrink: 0,
                          }}
                        />
                      )}
                      {showFeedback && isSelected && !isCorrectAnswer && (
                        <FiX
                          style={{
                            color: 'var(--liwaza-error)',
                            strokeWidth: 3,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>

              {isMulti && !showFeedback && (
                <button
                  onClick={handleMultiSelectSubmit}
                  disabled={selectedAnswers.length === 0}
                  style={{
                    background:
                      selectedAnswers.length > 0
                        ? 'var(--liwaza-primary)'
                        : 'var(--liwaza-outline-variant)',
                    color:
                      selectedAnswers.length > 0
                        ? 'white'
                        : 'var(--liwaza-on-surface-variant)',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor:
                      selectedAnswers.length > 0 ? 'pointer' : 'not-allowed',
                    marginTop: '1rem',
                    width: '100%',
                    transition: 'all 0.2s ease',
                  }}
                >
                  Submit Answer
                </button>
              )}
            </div>

            {showFeedback && (
              <div
                ref={feedbackRef}
                style={{
                  background: isCorrect
                    ? 'var(--liwaza-success-container)'
                    : 'var(--liwaza-error-container)',
                  color: isCorrect
                    ? 'var(--liwaza-on-success-container)'
                    : 'var(--liwaza-on-error-container)',
                  padding: 'clamp(1rem, 3vw, 1.5rem)',
                  borderRadius: '8px',
                  border: `1px solid ${isCorrect ? 'var(--liwaza-success)' : 'var(--liwaza-error)'}`,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                }}
              >
                <h3
                  style={{
                    marginBottom: '0.5rem',
                    fontSize: 'clamp(1rem, 3vw, 1.2rem)',
                    color: isCorrect
                      ? 'var(--liwaza-success)'
                      : 'var(--liwaza-error)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {isCorrect ? (
                    <>
                      <FiCheck style={{ strokeWidth: 3 }} />
                      Correct!
                    </>
                  ) : (
                    <>
                      <FiX style={{ strokeWidth: 3 }} />
                      Incorrect
                    </>
                  )}
                </h3>
                <p
                  style={{
                    fontSize: 'clamp(0.9rem, 2.5vw, 1rem)',
                    lineHeight: 1.5,
                    margin: 0,
                  }}
                >
                  {exercise.explanation}
                </p>

                <button
                  onClick={handleNextStep}
                  style={{
                    background: 'var(--liwaza-primary)',
                    color: 'white',
                    border: 'none',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    marginTop: '1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {exerciseIndex >= exercises.length - 1
                    ? 'Complete Article'
                    : 'Next Exercise'}
                  <FiArrowRight />
                </button>

                <div style={{ marginTop: '2rem' }}>
                  <GetStartedWith
                    title="Questions about this exercise?"
                    spaceName="GRE Vocabulary"
                    humanCoachTitle="Ask a GRE coach"
                    aifirstmessage={`I need help understanding the exercise "${exercise.question}"`}
                    aicontext={
                      'understanding an exercise with question: ' +
                      exercise.question +
                      ' and options: ' +
                      exercise.options
                    }
                    liwazaAITitle="Ask AI"
                    communityTitle="Ask other students"
                    showHumanCoach={true}
                    showLiwazaAI={true}
                    showCommunity={true}
                    style={{ border: 'none' }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="vocabulary-article">
      {authMismatch && (
        <div
          style={{
            maxWidth: '960px',
            margin: '1rem auto 0',
            background: 'rgba(220, 53, 69, 0.08)',
            border: '1px solid rgba(220, 53, 69, 0.3)',
            borderRadius: '12px',
            padding: '1rem 1.25rem',
            color: 'var(--liwaza-on-surface)',
          }}
        >
          <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
            Account mismatch detected
          </strong>
          <div style={{ marginBottom: '0.5rem' }}>
            Signed in as: <strong>{authMismatch.currentEmail || 'Unknown user'}</strong>
          </div>
          {authMismatch.requestedEmail && (
            <div style={{ marginBottom: '0.5rem' }}>
              Requested account:{' '}
              <strong>{authMismatch.requestedEmail}</strong>
            </div>
          )}
          <div style={{ marginBottom: '0.75rem' }}>{authMismatch.message}</div>
          <button
            onClick={() => logout()}
            style={{
              background: 'var(--liwaza-primary)',
              color: 'white',
              border: 'none',
              padding: '0.7rem 1rem',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Log out and switch account
          </button>
        </div>
      )}
      {toast.show && (
        <div
          className={`toast-notification ${toast.success ? 'success' : 'error'}`}
          style={{
            position: 'fixed',
            top: '24px',
            right: '24px',
            zIndex: 9999,
          }}
        >
          <div className="toast-icon">{toast.success ? '✓' : '⚠'}</div>
          {toast.message}
        </div>
      )}

      {currentStep === 'article' && renderArticle()}
      {currentStep === 'exercise' && renderExercise()}
    </div>
  );
}

export default VocabularyDashboard;
